// HRMS — Payroll: Components, Salary Structures, Payroll Runs, Reimbursements
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import hr from '../api';
import { PageHeader, Btn, Drawer, Input, Select, Table, Pill, fmtDate, fmtMoney, statusTone, SOFT, PANEL, Kpi } from '../ui';

// ─── Salary Structures (and Components inline) ─────────────────────────────
export function SalaryStructures({ companyId }) {
  const [tab, setTab] = useState('structures');
  return (
    <div data-testid="hr-salary">
      <PageHeader title="Salary Structures" subtitle="Components · CTC mapping" />
      <div className="flex gap-2 mb-4">
        {[{ k: 'structures', l: 'Employee Structures' }, { k: 'components', l: 'Components' }].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded text-xs tracking-[0.15em] uppercase ${tab === t.k ? 'bg-white text-neutral-900' : 'text-white/55 hover:text-white border border-white/10'}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === 'structures' && <StructuresTab companyId={companyId} />}
      {tab === 'components' && <ComponentsTab companyId={companyId} />}
    </div>
  );
}

function ComponentsTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'earning', calc_type: 'fixed' });
  const reload = async () => { if (companyId) setRows(await hr.get('/salary-components', { company_id: companyId })); };
  useEffect(() => { reload(); }, [companyId]);

  const save = async () => {
    try {
      if (!form.name || !form.code) return toast.error('Name and code required');
      await hr.post('/salary-components', { ...form, company_id: companyId, value: Number(form.value || 0) });
      toast.success('Saved'); setOpen(false); setForm({ type: 'earning', calc_type: 'fixed' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <>
      <div className="flex justify-end mb-3"><Btn variant="accent" onClick={() => setOpen(true)}>+ Component</Btn></div>
      <Table
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.code}</span> },
          { key: 'type', label: 'Type', render: (r) => <Pill tone={r.type === 'earning' ? 'green' : 'red'}>{r.type}</Pill> },
          { key: 'calc_type', label: 'Calc' },
          { key: 'value', label: 'Value', align: 'right', render: (r) => r.calc_type === 'fixed' ? fmtMoney(r.value) : `${r.value}%` },
          { key: 'a', label: '', render: (r) => <button className="text-xs text-rose-400/70" onClick={async () => { await hr.del(`/salary-components/${r.id}`); reload(); }}>Delete</button> }
        ]}
        rows={rows} empty="No components yet. Add Basic, HRA, Conveyance, PF, PT…"
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Salary Component" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Code *" value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="earning">Earning</option><option value="deduction">Deduction</option><option value="statutory">Statutory</option>
          </Select>
          <Select label="Calc" value={form.calc_type} onChange={(e) => setForm({ ...form, calc_type: e.target.value })}>
            <option value="fixed">Fixed Amount</option><option value="pct_of_basic">% of Basic</option><option value="pct_of_ctc">% of CTC</option>
          </Select>
          <Input label="Value" type="number" value={form.value || 0} onChange={(e) => setForm({ ...form, value: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Save</Btn></div>
      </Drawer>
    </>
  );
}

function StructuresTab({ companyId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [components, setComponents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ effective_date: new Date().toISOString().slice(0, 10), ctc_annual: 0, lines: [] });

  const reload = async () => {
    if (!companyId) return;
    setRows(await hr.get('/salary-structures'));
    setEmployees(await hr.get('/employees', { company_id: companyId }));
    setComponents(await hr.get('/salary-components', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const calcLines = (ctc) => {
    const monthly = (ctc || 0) / 12;
    const basic = Math.round(monthly * 0.4);
    const hra = Math.round(basic * 0.5);
    const remaining = monthly - basic - hra;
    return [
      { component_id: '', code: 'BASIC', name: 'Basic', type: 'earning', amount: basic },
      { component_id: '', code: 'HRA', name: 'HRA', type: 'earning', amount: hra },
      { component_id: '', code: 'SPL', name: 'Special Allowance', type: 'earning', amount: Math.max(remaining, 0) },
      { component_id: '', code: 'PF', name: 'PF', type: 'deduction', amount: Math.round(basic * 0.12) },
    ];
  };

  const newStruct = () => { setForm({ effective_date: new Date().toISOString().slice(0, 10), ctc_annual: 0, lines: [] }); setOpen(true); };
  const updateCtc = (v) => { const ctc = Number(v); setForm({ ...form, ctc_annual: ctc, lines: calcLines(ctc) }); };
  const save = async () => {
    try {
      if (!form.employee_id || !form.ctc_annual) return toast.error('Employee and CTC required');
      await hr.post('/salary-structures', form);
      toast.success('Saved'); setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e.full_name]));

  return (
    <>
      <div className="flex justify-end mb-3"><Btn variant="accent" onClick={newStruct} data-testid="hr-new-structure">+ Map Salary</Btn></div>
      <Table
        columns={[
          { key: 'employee_id', label: 'Employee', render: (r) => empMap[r.employee_id] || '—' },
          { key: 'ctc_annual', label: 'CTC (yr)', align: 'right', render: (r) => fmtMoney(r.ctc_annual) },
          { key: 'gross_monthly', label: 'Gross', align: 'right', render: (r) => fmtMoney(r.gross_monthly) },
          { key: 'net_monthly', label: 'Net (mo)', align: 'right', render: (r) => <span className="text-emerald-300 font-medium">{fmtMoney(r.net_monthly)}</span> },
          { key: 'effective_date', label: 'Effective', render: (r) => fmtDate(r.effective_date) },
        ]}
        rows={rows} empty="No salary structures yet."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="Map Salary Structure" width="max-w-2xl">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Select label="Employee *" value={form.employee_id || ''} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">—</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Select>
          <Input label="CTC Annual *" type="number" value={form.ctc_annual} onChange={(e) => updateCtc(e.target.value)} data-testid="struct-ctc" />
          <Input label="Effective" type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
        </div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-white/45 mb-2">Components (auto-suggested)</div>
        <div className={`${PANEL} rounded-lg overflow-hidden`}>
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-white/55 text-[10px] tracking-[0.2em] uppercase">
              <tr><th className="px-2 py-2 text-left">Component</th><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-right">Monthly Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {form.lines.map((l, i) => (
                <tr key={i}>
                  <td className="px-2 py-1.5 text-white">{l.name}</td>
                  <td className="px-2 py-1.5"><Pill tone={l.type === 'earning' ? 'green' : 'red'}>{l.type}</Pill></td>
                  <td className="px-2 py-1.5 text-right">
                    <input className="bg-white/[0.04] border border-white/10 rounded px-2 py-1 text-xs w-32 text-right tabular-nums text-white"
                      type="number" value={l.amount} onChange={(e) => {
                        const lines = [...form.lines]; lines[i] = { ...l, amount: Number(e.target.value) || 0 }; setForm({ ...form, lines });
                      }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save} data-testid="struct-save">Save</Btn></div>
      </Drawer>
    </>
  );
}

// ─── Payroll Runs ──────────────────────────────────────────────────────────
export function PayrollRuns({ companyId }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const now = new Date();
  const [form, setForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });
  const reload = async () => { if (companyId) setRows(await hr.get('/payroll-runs', { company_id: companyId })); };
  useEffect(() => { reload(); }, [companyId]);

  const create = async () => {
    try {
      await hr.post('/payroll-runs', { ...form, company_id: companyId, month: Number(form.month), year: Number(form.year) });
      toast.success('Payroll run created'); setOpen(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const openDetail = async (rid) => setDetail(await hr.get(`/payroll-runs/${rid}`));
  const approve = async (rid) => { await hr.post(`/payroll-runs/${rid}/approve`); toast.success('Approved'); openDetail(rid); reload(); };

  return (
    <div data-testid="hr-payroll">
      <PageHeader title="Payroll Runs" subtitle="Monthly payslips · loss-of-pay aware"
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="hr-new-payrun">+ New Run</Btn>} />
      <Table
        columns={[
          { key: 'run_no', label: '#', render: (r) => <button onClick={() => openDetail(r.id)} className="font-mono text-xs text-white/80 underline-offset-2 hover:underline">{r.run_no}</button> },
          { key: 'period', label: 'Period', render: (r) => `${r.month}/${r.year}` },
          { key: 'total_payslips', label: 'Payslips', align: 'right' },
          { key: 'total_gross', label: 'Gross', align: 'right', render: (r) => fmtMoney(r.total_gross) },
          { key: 'total_net', label: 'Net', align: 'right', render: (r) => <span className="text-emerald-300 font-medium">{fmtMoney(r.total_net)}</span> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
        ]}
        rows={rows} empty="No payroll runs yet."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Payroll Run" width="max-w-md">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}>
            {[...Array(12)].map((_, i) => <option key={i+1} value={i+1}>{new Date(2000, i, 1).toLocaleString('en', { month: 'long' })}</option>)}
          </Select>
          <Input label="Year" type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={create}>Run Payroll</Btn></div>
      </Drawer>
      <Drawer open={!!detail} onClose={() => setDetail(null)} title={detail ? `Payroll ${detail.run_no}` : ''} width="max-w-5xl">
        {detail && (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <Kpi label="Payslips" value={detail.total_payslips} />
              <Kpi label="Gross" value={fmtMoney(detail.total_gross)} />
              <Kpi label="Net" value={fmtMoney(detail.total_net)} tone="green" />
              <Kpi label="Status" value={<Pill tone={statusTone(detail.status)}>{detail.status}</Pill>} />
            </div>
            {detail.status === 'draft' && <div className="mb-3"><Btn variant="accent" onClick={() => approve(detail.id)} data-testid="payrun-approve">Approve Run</Btn></div>}
            <Table
              columns={[
                { key: 'emp_code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/55">{r.emp_code}</span> },
                { key: 'employee_name', label: 'Employee' },
                { key: 'gross', label: 'Gross', align: 'right', render: (r) => fmtMoney(r.gross) },
                { key: 'deductions', label: 'Deductions', align: 'right', render: (r) => fmtMoney(r.deductions) },
                { key: 'lop_days', label: 'LOP', align: 'right' },
                { key: 'net', label: 'Net', align: 'right', render: (r) => <span className="text-emerald-300 font-medium">{fmtMoney(r.net)}</span> },
              ]}
              rows={detail.payslips || []}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}

// ─── Reimbursements ────────────────────────────────────────────────────────
export function Reimbursements({ companyId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: 'travel' });
  const reload = async () => {
    setRows(await hr.get('/reimbursements'));
    if (companyId) setEmployees(await hr.get('/employees', { company_id: companyId }));
  };
  useEffect(() => { reload(); }, [companyId]);

  const save = async () => {
    try {
      if (!form.employee_id || !form.amount || !form.expense_date) return toast.error('Employee, amount, date required');
      await hr.post('/reimbursements', { ...form, amount: Number(form.amount) });
      toast.success('Submitted'); setOpen(false); setForm({ category: 'travel' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const decide = async (id, action) => {
    await hr.post(`/reimbursements/${id}/decide`, { action });
    toast.success(action === 'approve' ? 'Approved' : 'Rejected'); reload();
  };

  const empMap = Object.fromEntries(employees.map((e) => [e.id, e.full_name]));
  return (
    <div data-testid="hr-reimb">
      <PageHeader title="Reimbursements" subtitle="Employee expense claims"
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="hr-new-reimb">+ Claim</Btn>} />
      <Table
        columns={[
          { key: 'expense_date', label: 'Date', render: (r) => fmtDate(r.expense_date) },
          { key: 'employee_id', label: 'Employee', render: (r) => empMap[r.employee_id] || '—' },
          { key: 'category', label: 'Category', render: (r) => <Pill tone="blue">{r.category}</Pill> },
          { key: 'amount', label: 'Amount', align: 'right', render: (r) => <span className="tabular-nums font-medium">{fmtMoney(r.amount)}</span> },
          { key: 'description', label: 'Description', render: (r) => <span className="text-white/55 text-xs">{r.description || ''}</span> },
          { key: 'status', label: 'Status', render: (r) => <Pill tone={statusTone(r.status)}>{r.status}</Pill> },
          { key: 'a', label: '', render: (r) => r.status === 'pending' ? (<div className="flex gap-2 justify-end"><button className="text-xs text-emerald-300" onClick={() => decide(r.id, 'approve')}>Approve</button><button className="text-xs text-rose-400" onClick={() => decide(r.id, 'reject')}>Reject</button></div>) : null },
        ]}
        rows={rows} empty="No claims."
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Reimbursement" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Employee *" value={form.employee_id || ''} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">—</option>{employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </Select>
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="travel">Travel</option><option value="food">Food</option><option value="fuel">Fuel</option>
            <option value="office">Office</option><option value="internet">Internet</option><option value="other">Other</option>
          </Select>
          <Input label="Amount *" type="number" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <Input label="Date *" type="date" value={form.expense_date || ''} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} />
        </div>
        <Input label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={save}>Submit</Btn></div>
      </Drawer>
    </div>
  );
}
